import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  ArrowRight, ChevronDown, MapPin, TrendingUp, Building2, Award,
  Star, Phone, Calculator, Zap, Shield, Globe, Users, Plane,
  Train, Landmark, CheckCircle, BarChart2, Clock, X, Sparkles
} from "lucide-react";
import { HOME, FORMS } from "@/constants/testIds";
import MarqueeTicker from "@/components/MarqueeTicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

// ======================== SECTION 1: HERO ========================
const HeroSection = () => (
  <section
    className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
    style={{
      background: `linear-gradient(180deg, rgba(8,8,8,0.65) 0%, rgba(8,8,8,0.82) 60%, #080808 100%),
                   url('https://images.pexels.com/photos/22812019/pexels-photo-22812019.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920') center/cover no-repeat`,
    }}
  >
    {/* Animated orbs */}
    <div className="orb orb-1" style={{ width: 500, height: 500, top: "5%", left: "10%" }} />
    <div className="orb orb-2" style={{ width: 400, height: 400, bottom: "15%", right: "8%" }} />
    <div className="orb orb-3" style={{ width: 300, height: 300, top: "40%", left: "55%" }} />

    {/* Grid overlay */}
    <div className="absolute inset-0 pointer-events-none opacity-[0.03]"
      style={{
        backgroundImage: "linear-gradient(rgba(212,175,55,1) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,1) 1px, transparent 1px)",
        backgroundSize: "80px 80px",
      }} />

    {/* Gold particles */}
    {[...Array(14)].map((_, i) => (
      <div key={i} className="particle"
        style={{
          left: `${5 + i * 7}%`, top: `${10 + (i % 4) * 22}%`,
          width: i % 3 === 0 ? "3px" : "2px",
          height: i % 3 === 0 ? "3px" : "2px",
          "--duration": `${2.5 + i * 0.6}s`, "--delay": `${i * 0.3}s`,
          opacity: 0.4 + (i % 3) * 0.2,
        }} />
    ))}

    {/* Center ambient glow */}
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full pointer-events-none"
      style={{ background: "radial-gradient(circle, rgba(212,175,55,0.06) 0%, transparent 65%)" }} />

    <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
      {/* Logo in hero */}
      <motion.div
        initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
        className="flex justify-center mb-6"
      >
        <img
          src="/sneha-logo.jpg"
          alt="Sneha Reality"
          className="h-24 md:h-28 w-auto object-contain"
          style={{ filter: "drop-shadow(0 0 20px rgba(212,175,55,0.35))" }}
        />
      </motion.div>

      <motion.h1
        data-testid={HOME.heroHeading}
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 1 }}
        className="font-display text-5xl sm:text-6xl lg:text-7xl font-light text-[#FAF8F3] leading-[1.1] mb-6"
      >
        Invest In Tomorrow.
        <span className="block text-gold-gradient italic mt-1">Prosper Forever.</span>
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}
        className="text-[#A0A0A0] text-base md:text-lg font-light leading-relaxed mb-10 max-w-2xl mx-auto"
      >
        Strategic real estate investments backed by infrastructure, connectivity and future demand.
        India's most premium advisory platform for Navi Mumbai.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }}
        className="flex flex-col sm:flex-row gap-4 justify-center"
      >
        <Link to="/properties" data-testid={HOME.heroCtaPrimary} className="btn-gold-solid btn-gold px-10 py-4">
          Explore Opportunities <ArrowRight size={14} className="inline ml-2" />
        </Link>
        <Link to="/consultation" data-testid={HOME.heroCtaSecondary} className="btn-gold px-10 py-4">
          Book Private Consultation
        </Link>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]/50">Scroll</span>
        <div className="w-[1px] h-10 bg-gradient-to-b from-[#D4AF37]/40 to-transparent relative overflow-hidden">
          <div className="w-full h-4 bg-[#D4AF37] animate-scroll-dot" />
        </div>
      </motion.div>
    </div>
  </section>
);

// ======================== SECTION 2: WEALTH COUNTER ========================
const CounterNum = ({ target, suffix, prefix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const end = parseInt(target.replace(/[^0-9]/g, "")) || 0;
    if (end === 0) return;
    let start = 0;
    const step = Math.ceil(end / 60);
    const timer = setInterval(() => {
      start = Math.min(start + step, end);
      setCount(start);
      if (start >= end) clearInterval(timer);
    }, 25);
    return () => clearInterval(timer);
  }, [inView, target]);

  return (
    <span ref={ref} className="font-display text-4xl md:text-5xl font-light text-[#FAF8F3]">
      {prefix}{count}{suffix}
    </span>
  );
};

const WealthCounter = () => {
  const metrics = [
    { value: "1000", prefix: "₹", suffix: "+ Cr", label: "Assets Under Advisory", icon: TrendingUp },
    { value: "500", prefix: "", suffix: "+", label: "Investors Served", icon: Users },
    { value: "100", prefix: "", suffix: "%", label: "Verified Assets", icon: Shield },
    { value: "0", prefix: "", suffix: "%", label: "Hidden Charges", icon: CheckCircle },
  ];

  return (
    <section data-testid={HOME.wealthCounter} className="py-20 relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #0a0900 0%, #0d0b00 50%, #0a0900 100%)" }}
    >
      <div className="absolute inset-0 opacity-20"
        style={{ background: "radial-gradient(ellipse 80% 50% at 50% 50%, rgba(212,175,55,0.12), transparent)" }} />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
          {metrics.map((m, i) => (
            <motion.div
              key={m.label}
              {...fadeUp}
              transition={{ duration: 0.8, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="text-center"
            >
              <div className="flex justify-center mb-4">
                <div className="w-10 h-10 border border-[#D4AF37]/20 flex items-center justify-center">
                  <m.icon size={18} className="text-[#D4AF37]" />
                </div>
              </div>
              <CounterNum target={m.value} prefix={m.prefix} suffix={m.suffix} />
              <p className="text-[#666] text-xs tracking-[0.2em] uppercase font-heading mt-2">{m.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ======================== SECTION 3: INFRASTRUCTURE MAP ========================
const InfrastructureMap = () => {
  const [activeZone, setActiveZone] = useState(null);
  const [infraData, setInfraData] = useState([]);

  useEffect(() => {
    axios.get(`${API}/infrastructure`).then((r) => setInfraData(r.data)).catch(() => {});
  }, []);

  const zones = infraData.length ? infraData : [
    { id: "airport", short: "NMIA Airport", icon: "plane", position: { x: 70, y: 62 },
      status: "Under Construction", metrics: [
        { label: "Appreciation Potential", value: "+200%" },
        { label: "Jobs Created", value: "2.5L+" },
      ],
      description: "India's largest greenfield airport – primary catalyst for Navi Mumbai appreciation."
    },
    { id: "atal-setu", short: "Atal Setu", icon: "bridge", position: { x: 18, y: 40 },
      status: "Operational 2024", metrics: [
        { label: "Time Reduction", value: "2hr → 20min" },
        { label: "Property Premium", value: "+35%" },
      ],
      description: "India's longest sea bridge connecting Mumbai to Navi Mumbai in 20 minutes."
    },
    { id: "metro", short: "Metro Lines", icon: "train", position: { x: 42, y: 30 },
      status: "Operational + Expanding", metrics: [
        { label: "Rental Premium", value: "+40%" },
        { label: "Stations", value: "36+" },
      ],
      description: "Expanding metro creating premium rental corridors across Navi Mumbai."
    },
    { id: "naina", short: "NAINA Zone", icon: "city", position: { x: 58, y: 78 },
      status: "Development Phase", metrics: [
        { label: "Area", value: "30,000 Ha" },
        { label: "Investment", value: "₹3L Cr" },
      ],
      description: "NAINA smart city: 30,000 hectares of planned development by 2035."
    },
  ];

  const iconMap = { plane: Plane, bridge: Landmark, train: Train, city: Building2 };

  return (
    <section className="py-24 md:py-32 bg-[#080808]" data-testid={HOME.infraMap}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <motion.p {...fadeUp} className="section-label">Navi Mumbai Growth Engine</motion.p>
          <motion.h2 {...fadeUp} transition={{ delay: 0.1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-display text-4xl md:text-5xl font-light text-[#FAF8F3] max-w-xl mx-auto">
            4 Forces Driving<br />
            <span className="text-gold-gradient italic">India's Richest Corridor</span>
          </motion.h2>
          <motion.p {...fadeUp} transition={{ delay: 0.2 }} className="text-[#666] text-sm mt-4 max-w-lg mx-auto">
            Click each hotspot to explore how each infrastructure project creates measurable wealth for early investors.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Map visual — real aerial background */}
          <motion.div {...fadeUp} className="relative aspect-[4/3] rounded-sm overflow-hidden border border-[#D4AF37]/15"
            style={{ boxShadow: "0 0 60px rgba(212,175,55,0.06)" }}>
            {/* Real aerial image of Navi Mumbai */}
            <img
              src="https://images.pexels.com/photos/5050812/pexels-photo-5050812.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Navi Mumbai Region"
              className="w-full h-full object-cover"
              style={{ filter: "brightness(0.45) saturate(0.6)" }}
            />
            {/* Gold tint overlay */}
            <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 60% 60%, rgba(212,175,55,0.08) 0%, transparent 70%)" }} />
            {/* Grid overlay */}
            <div className="absolute inset-0 opacity-[0.04]"
              style={{
                backgroundImage: "linear-gradient(rgba(212,175,55,1) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,1) 1px, transparent 1px)",
                backgroundSize: "40px 40px"
              }} />
            <div className="absolute inset-0">
              <div className="relative w-full h-full p-6">
                {/* Region label */}
                <div className="absolute top-4 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.3em] text-[#D4AF37]/50 uppercase font-heading bg-black/40 px-3 py-1 border border-[#D4AF37]/10 backdrop-blur-sm whitespace-nowrap">
                  NAVI MUMBAI METROPOLITAN REGION
                </div>

                {/* Mumbai coast line indicator */}
                <div className="absolute left-[15%] top-[20%] bottom-[20%] w-[1px] bg-gradient-to-b from-transparent via-[#D4AF37]/30 to-transparent" />
                <div className="absolute left-[13%] top-1/2 -translate-y-1/2 text-[8px] text-[#D4AF37]/30 -rotate-90 whitespace-nowrap tracking-[0.2em] uppercase">Mumbai Coast</div>

                {/* Hotspots */}
                {zones.map((zone) => {
                  const Icon = iconMap[zone.icon] || Building2;
                  const isActive = activeZone?.id === zone.id;
                  return (
                    <button
                      key={zone.id}
                      data-testid={`infra-hotspot-${zone.id}`}
                      onClick={() => setActiveZone(isActive ? null : zone)}
                      className="absolute transform -translate-x-1/2 -translate-y-1/2 group z-10"
                      style={{ left: `${zone.position.x}%`, top: `${zone.position.y}%` }}
                    >
                      {/* Outer pulse rings */}
                      {!isActive && (
                        <>
                          <div className="absolute inset-[-16px] rounded-full border border-[#D4AF37]/10 animate-ping" style={{ animationDuration: "2.5s" }} />
                          <div className="absolute inset-[-8px] rounded-full border border-[#D4AF37]/20 animate-ping" style={{ animationDuration: "2.5s", animationDelay: "0.4s" }} />
                        </>
                      )}
                      <div className={`relative flex items-center justify-center w-11 h-11 rounded-full transition-all duration-300 ${
                        isActive
                          ? "bg-[#D4AF37] scale-125 shadow-[0_0_24px_rgba(212,175,55,0.6)]"
                          : "bg-black/60 border border-[#D4AF37]/50 hover:bg-[#D4AF37]/20 backdrop-blur-sm"
                      }`}>
                        <Icon size={16} className={isActive ? "text-black" : "text-[#D4AF37]"} />
                      </div>
                      <div className={`absolute top-full mt-2 left-1/2 -translate-x-1/2 text-[9px] tracking-wider text-center whitespace-nowrap transition-all px-2 py-1 rounded-sm ${
                        isActive
                          ? "text-[#D4AF37] bg-black/70 border border-[#D4AF37]/30 backdrop-blur-sm"
                          : "text-[#A0A0A0]"
                      }`}>
                        {zone.short}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>

          {/* Info panel */}
          <div className="space-y-4">
            <AnimatePresence mode="wait">
              {activeZone ? (
                <motion.div
                  key={activeZone.id}
                  initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                  className="glass-gold p-8"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center shrink-0">
                      {React.createElement(iconMap[activeZone.icon] || Building2, { size: 18, className: "text-[#D4AF37]" })}
                    </div>
                    <div>
                      <h3 className="font-heading text-lg text-[#FAF8F3] font-light">{activeZone.short}</h3>
                      <span className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37] bg-[#D4AF37]/5 border border-[#D4AF37]/20 px-2 py-0.5">{activeZone.status}</span>
                    </div>
                  </div>
                  <p className="text-[#A0A0A0] text-sm leading-relaxed mb-6">{activeZone.description}</p>
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    {(activeZone.metrics || []).map((m) => (
                      <div key={m.label} className="border border-[#D4AF37]/10 p-4 bg-[#D4AF37]/[0.02]">
                        <p className="text-[#D4AF37] text-2xl font-display font-light">{m.value}</p>
                        <p className="text-[#555] text-[10px] tracking-wider uppercase mt-1">{m.label}</p>
                      </div>
                    ))}
                  </div>
                  <Link to="/properties" className="btn-gold inline-flex mt-2 text-xs py-3 px-6">
                    View Properties in Zone <ArrowRight size={12} className="ml-2" />
                  </Link>
                </motion.div>
              ) : (
                <motion.div key="default" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
                  <p className="text-[#666] text-xs leading-relaxed mb-6 tracking-wide uppercase border-l-2 border-[#D4AF37]/30 pl-4">
                    Navi Mumbai is powered by 4 transformational infrastructure projects. Click a hotspot on the map to explore each catalyst.
                  </p>
                  {zones.map((zone, idx) => {
                    const Icon = iconMap[zone.icon] || Building2;
                    return (
                      <button key={zone.id} onClick={() => setActiveZone(zone)}
                        className="w-full glass-gold p-4 flex items-center gap-4 hover:border-[#D4AF37]/40 transition-all duration-300 group text-left"
                      >
                        <div className="w-9 h-9 border border-[#D4AF37]/20 flex items-center justify-center shrink-0 group-hover:border-[#D4AF37]/50 group-hover:bg-[#D4AF37]/5 transition-all">
                          <Icon size={14} className="text-[#D4AF37]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-[#FAF8F3] font-light">{zone.short}</p>
                          <p className="text-[10px] text-[#555] tracking-wider uppercase mt-0.5">{zone.status}</p>
                        </div>
                        <div className="text-[9px] text-[#D4AF37]/40 tracking-[0.2em] mr-2">0{idx + 1}</div>
                        <ArrowRight size={13} className="text-[#D4AF37]/20 group-hover:text-[#D4AF37] transition-colors shrink-0" />
                      </button>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

// ======================== SECTION 4: WHY NAVI MUMBAI ========================
const WhyNaviMumbaiSection = () => {
  const timeline = [
    { year: "2005", label: "Mumbai Era", desc: "Real estate peak in South Mumbai and suburbs", highlight: false },
    { year: "2024", label: "The Shift", desc: "Atal Setu + Metro + Airport announcement: Smart money moves", highlight: true },
    { year: "2035", label: "Global Corridor", desc: "Navi Mumbai: India's premier investment destination", highlight: false },
  ];

  const stats = [
    { value: "4X", label: "Lower land cost vs South Mumbai" },
    { value: "85%+", label: "Avg appreciation in 5 years" },
    { value: "6 Cr", label: "Airport passenger capacity/year" },
    { value: "2035", label: "Target: Global investment corridor" },
  ];

  return (
    <section className="py-24 md:py-32 bg-[#060606]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <motion.p {...fadeUp} className="section-label">Market Intelligence</motion.p>
            <motion.h2 {...fadeUp} transition={{ delay: 0.1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="font-display text-4xl md:text-5xl font-light text-[#FAF8F3] mb-6">
              India's Next<br />
              <span className="text-gold-gradient italic">Billion Dollar Shift</span>
            </motion.h2>
            <motion.p {...fadeUp} transition={{ delay: 0.2 }}
              className="text-[#A0A0A0] text-sm leading-relaxed mb-10">
              Smart money has already identified Navi Mumbai as the next major appreciation corridor. Infrastructure investment of over ₹3 lakh crore is transforming this region into India's most connected city.
            </motion.p>

            {/* Timeline */}
            <div className="relative pl-6">
              <div className="absolute left-0 top-0 bottom-0 w-[1px] bg-gradient-to-b from-[#D4AF37]/10 via-[#D4AF37]/40 to-[#D4AF37]/10" />
              {timeline.map((t, i) => (
                <motion.div key={t.year}
                  {...fadeUp} transition={{ delay: 0.2 + i * 0.15, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                  className="relative pb-8 last:pb-0"
                >
                  <div className={`absolute -left-[21px] w-3 h-3 rounded-full border-2 ${
                    t.highlight ? "bg-[#D4AF37] border-[#D4AF37]" : "bg-[#080808] border-[#D4AF37]/40"
                  }`} />
                  <p className="text-xs text-[#D4AF37] tracking-[0.2em] mb-1">{t.year}</p>
                  <p className="text-[#FAF8F3] font-light text-sm mb-1">{t.label}</p>
                  <p className="text-[#666] text-xs leading-relaxed">{t.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-4">
            {stats.map((s, i) => (
              <motion.div key={s.label}
                {...fadeUp} transition={{ delay: 0.15 * i, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="glass-gold p-6 gold-card"
              >
                <p className="font-display text-3xl text-[#D4AF37] font-light mb-2">{s.value}</p>
                <p className="text-[#666] text-xs tracking-wider leading-relaxed">{s.label}</p>
              </motion.div>
            ))}
            <motion.div
              {...fadeUp} transition={{ delay: 0.6 }}
              className="col-span-2 glass-gold p-6 border border-[#D4AF37]/10"
            >
              <div className="flex items-center gap-3 mb-3">
                <BarChart2 size={16} className="text-[#D4AF37]" />
                <p className="text-xs text-[#D4AF37] tracking-wider uppercase">Appreciation Trajectory</p>
              </div>
              <div className="flex items-end gap-2 h-16">
                {[30, 45, 55, 70, 85, 100].map((h, i) => (
                  <motion.div key={i}
                    initial={{ scaleY: 0 }} whileInView={{ scaleY: 1 }}
                    viewport={{ once: true }} transition={{ delay: 0.8 + i * 0.1, duration: 0.6 }}
                    className="flex-1 rounded-t-sm origin-bottom"
                    style={{ height: `${h}%`, background: `linear-gradient(180deg, #D4AF37 0%, #F4D98B ${100 - h}%)`, opacity: 0.6 + i * 0.07 }}
                  />
                ))}
              </div>
              <div className="flex justify-between mt-2">
                {["2019", "2020", "2021", "2022", "2023", "2024"].map((y) => (
                  <span key={y} className="text-[9px] text-[#444]">{y}</span>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ======================== SECTION 5: ROI CALCULATOR ========================
const ROICalculator = () => {
  const [activeTab, setActiveTab] = useState("roi");

  // ── Shared state ──
  const [budget, setBudget] = useState(5000000);
  const [years, setYears] = useState(5);

  // ── ROI tab state ──
  const [propType, setPropType] = useState("residential");
  const [leadCapture, setLeadCapture] = useState(false);
  const [leadName, setLeadName] = useState("");
  const [leadPhone, setLeadPhone] = useState("");
  const [submitted, setSubmitted] = useState(false);

  // ── EMI tab state ──
  const [downPct, setDownPct] = useState(20);
  const [tenure, setTenure] = useState(20);
  const [interestRate, setInterestRate] = useState(8.5);

  // ── ROI calculations ──
  const typeRates = {
    residential: { rate: 0.12, label: "Residential", desc: "Apartments & Flats" },
    prelaunch:   { rate: 0.18, label: "Pre-launch",  desc: "Early access units" },
    commercial:  { rate: 0.10, label: "Commercial",  desc: "Office & Retail" },
    villa:       { rate: 0.14, label: "Villa / Row House", desc: "Premium villas" },
  };
  const rate = typeRates[propType].rate;
  const projectedValue = Math.round(budget * Math.pow(1 + rate, years));
  const totalROI = Math.round(((projectedValue - budget) / budget) * 100);
  const rentalYield = Math.round(budget * 0.042 * years);

  // ── EMI calculations ──
  const downPayment = Math.round(budget * downPct / 100);
  const loanAmount = budget - downPayment;
  const monthlyRate = interestRate / 12 / 100;
  const n = tenure * 12;
  const emi = n > 0 && monthlyRate > 0
    ? Math.round((loanAmount * monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1))
    : Math.round(loanAmount / n);
  const totalPayable = emi * n;
  const totalInterest = totalPayable - loanAmount;

  const formatINR = (num) => {
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000)   return `₹${(num / 100000).toFixed(0)}L`;
    return `₹${num.toLocaleString("en-IN")}`;
  };

  // Year-wise EMI breakdown for sparkline
  const projections = Array.from({ length: years }, (_, i) => ({
    yr: new Date().getFullYear() + i + 1,
    val: Math.round(budget * Math.pow(1 + rate, i + 1)),
  }));
  const maxVal = projections[projections.length - 1]?.val || budget;

  const handleCalculate = () => setLeadCapture(true);
  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/leads`, {
        lead_type: "roi_calculator",
        name: leadName, phone: leadPhone,
        budget: `₹${(budget / 100000).toFixed(0)}L`,
        timeline: `${years} years`,
        property_type: typeRates[propType].label,
        message: `Investment Simulator — ${typeRates[propType].label}, ${years}yr, Expected ROI: ${totalROI}%`
      });
    } catch { /* silent */ }
    setLeadCapture(false);
    setSubmitted(true);
  };

  return (
    <section id="roi-calculator" data-testid={HOME.roiCalculator} className="py-24 md:py-32 bg-[#080808]">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <motion.p {...fadeUp} className="section-label">Investment Simulator</motion.p>
          <motion.h2 {...fadeUp} transition={{ delay: 0.1 }}
            className="font-display text-4xl md:text-5xl font-light text-[#FAF8F3]">
            Engineer Your<br />
            <span className="text-gold-gradient italic">Wealth Trajectory</span>
          </motion.h2>
          <motion.p {...fadeUp} transition={{ delay: 0.2 }} className="text-[#666] text-sm mt-4 max-w-lg mx-auto">
            Model your investment with real Navi Mumbai appreciation data across property types and timelines.
          </motion.p>
        </div>

        <motion.div {...fadeUp} className="glass-gold p-8 md:p-12">
          {/* Tab switcher */}
          <div className="flex gap-0 mb-10 border border-white/10 w-fit">
            {[
              { key: "roi", label: "ROI Calculator" },
              { key: "emi", label: "EMI Calculator" },
            ].map((tab) => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`px-8 py-3 text-xs tracking-[0.2em] uppercase font-heading transition-all duration-300 ${
                  activeTab === tab.key
                    ? "bg-[#D4AF37] text-black"
                    : "text-[#666] hover:text-[#FAF8F3] hover:bg-white/5"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* ─── ROI TAB ─── */}
          {activeTab === "roi" && (
            <>
              {/* Property Type */}
              <div className="mb-8">
                <label className="text-xs tracking-[0.2em] text-[#D4AF37] uppercase mb-4 block">Property Type</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(typeRates).map(([key, t]) => (
                    <button key={key} onClick={() => setPropType(key)}
                      className={`p-4 border text-left transition-all duration-300 ${
                        propType === key
                          ? "border-[#D4AF37] bg-[#D4AF37]/5 shadow-[0_0_20px_rgba(212,175,55,0.08)]"
                          : "border-white/10 hover:border-white/20"
                      }`}
                    >
                      <p className={`text-sm font-light ${propType === key ? "text-[#D4AF37]" : "text-[#FAF8F3]"}`}>{t.label}</p>
                      <p className="text-[10px] text-[#555] mt-1">{t.desc}</p>
                      <p className={`text-xs mt-2 font-display ${propType === key ? "text-[#F4D98B]" : "text-[#444]"}`}>{(t.rate * 100).toFixed(0)}% p.a.</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div>
                  <label className="text-xs tracking-[0.2em] text-[#D4AF37] uppercase mb-3 block">Investment Budget</label>
                  <p className="font-display text-2xl text-[#FAF8F3] mb-3">{formatINR(budget)}</p>
                  <input type="range" min={2500000} max={50000000} step={500000} value={budget}
                    onChange={(e) => setBudget(Number(e.target.value))}
                    className="w-full h-[2px] bg-white/10 appearance-none rounded-full cursor-pointer"
                    style={{ accentColor: "#D4AF37" }} />
                  <div className="flex justify-between text-[10px] text-[#444] mt-1"><span>₹25L</span><span>₹5 Cr</span></div>
                </div>
                <div>
                  <label className="text-xs tracking-[0.2em] text-[#D4AF37] uppercase mb-3 block">Investment Horizon</label>
                  <p className="font-display text-2xl text-[#FAF8F3] mb-3">{years} Years</p>
                  <input type="range" min={1} max={10} step={1} value={years}
                    onChange={(e) => setYears(Number(e.target.value))}
                    className="w-full h-[2px] bg-white/10 appearance-none rounded-full cursor-pointer"
                    style={{ accentColor: "#D4AF37" }} />
                  <div className="flex justify-between text-[10px] text-[#444] mt-1"><span>1 yr</span><span>10 yrs</span></div>
                </div>
              </div>

              {/* Appreciation sparkline */}
              <div className="border border-white/5 p-6 mb-8 bg-[#0a0900]/50">
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#555] mb-4">Appreciation Curve — {typeRates[propType].label}</p>
                <div className="flex items-end gap-1 h-20 w-full">
                  {projections.map((p, i) => (
                    <motion.div key={i}
                      initial={{ scaleY: 0 }} whileInView={{ scaleY: 1 }}
                      viewport={{ once: true }} transition={{ delay: i * 0.07, duration: 0.5 }}
                      className="flex-1 origin-bottom rounded-t-sm"
                      style={{ height: `${(p.val / maxVal) * 100}%`, background: "linear-gradient(180deg, #D4AF37 0%, #F4D98B 70%)", opacity: 0.4 + (i / projections.length) * 0.6, minHeight: "8px" }}
                    />
                  ))}
                </div>
                <div className="flex justify-between mt-2">
                  <span className="text-[9px] text-[#444]">{projections[0]?.yr}</span>
                  {projections.length > 2 && <span className="text-[9px] text-[#444]">{projections[Math.floor(projections.length / 2)]?.yr}</span>}
                  <span className="text-[9px] text-[#D4AF37]">{projections[projections.length - 1]?.yr}</span>
                </div>
              </div>

              <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  <div>
                    <p className="text-[10px] tracking-[0.2em] text-[#666] uppercase mb-1">Projected Value</p>
                    <p className="font-display text-3xl text-[#D4AF37]">{formatINR(projectedValue)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.2em] text-[#666] uppercase mb-1">Total ROI</p>
                    <p className="font-display text-3xl text-[#FAF8F3]">{totalROI}%</p>
                  </div>
                  {submitted && (
                    <div>
                      <p className="text-[10px] tracking-[0.2em] text-[#666] uppercase mb-1">Est. Rental ({years}yr)</p>
                      <p className="font-display text-3xl text-[#FAF8F3]">{formatINR(rentalYield)}</p>
                    </div>
                  )}
                </div>
                {!submitted ? (
                  <button onClick={handleCalculate} className="btn-gold-solid btn-gold shrink-0">
                    Get Full Report <ArrowRight size={14} className="inline ml-2" />
                  </button>
                ) : (
                  <Link to="/consultation" className="btn-gold text-xs py-3 px-6 shrink-0">
                    Get Expert Advisory <ArrowRight size={12} className="inline ml-2" />
                  </Link>
                )}
              </div>
            </>
          )}

          {/* ─── EMI TAB ─── */}
          {activeTab === "emi" && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                {/* Property Value */}
                <div>
                  <label className="text-xs tracking-[0.2em] text-[#D4AF37] uppercase mb-3 block">Property Value</label>
                  <p className="font-display text-2xl text-[#FAF8F3] mb-3">{formatINR(budget)}</p>
                  <input type="range" min={2500000} max={50000000} step={500000} value={budget}
                    onChange={(e) => setBudget(Number(e.target.value))}
                    className="w-full h-[2px] bg-white/10 appearance-none rounded-full cursor-pointer"
                    style={{ accentColor: "#D4AF37" }} />
                  <div className="flex justify-between text-[10px] text-[#444] mt-1"><span>₹25L</span><span>₹5 Cr</span></div>
                </div>

                {/* Interest Rate */}
                <div>
                  <label className="text-xs tracking-[0.2em] text-[#D4AF37] uppercase mb-3 block">Home Loan Interest Rate</label>
                  <p className="font-display text-2xl text-[#FAF8F3] mb-3">{interestRate.toFixed(1)}% p.a.</p>
                  <input type="range" min={7} max={12} step={0.1} value={interestRate}
                    onChange={(e) => setInterestRate(Number(e.target.value))}
                    className="w-full h-[2px] bg-white/10 appearance-none rounded-full cursor-pointer"
                    style={{ accentColor: "#D4AF37" }} />
                  <div className="flex justify-between text-[10px] text-[#444] mt-1"><span>7%</span><span>12%</span></div>
                </div>

                {/* Down Payment */}
                <div>
                  <label className="text-xs tracking-[0.2em] text-[#D4AF37] uppercase mb-3 block">Down Payment</label>
                  <p className="font-display text-2xl text-[#FAF8F3] mb-3">{downPct}% — {formatINR(downPayment)}</p>
                  <div className="flex gap-2">
                    {[10, 20, 30, 40].map((pct) => (
                      <button key={pct} onClick={() => setDownPct(pct)}
                        className={`flex-1 py-2 text-xs border transition-all duration-200 ${
                          downPct === pct ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5" : "border-white/10 text-[#555]"
                        }`}
                      >
                        {pct}%
                      </button>
                    ))}
                  </div>
                </div>

                {/* Loan Tenure */}
                <div>
                  <label className="text-xs tracking-[0.2em] text-[#D4AF37] uppercase mb-3 block">Loan Tenure</label>
                  <p className="font-display text-2xl text-[#FAF8F3] mb-3">{tenure} Years</p>
                  <input type="range" min={5} max={30} step={5} value={tenure}
                    onChange={(e) => setTenure(Number(e.target.value))}
                    className="w-full h-[2px] bg-white/10 appearance-none rounded-full cursor-pointer"
                    style={{ accentColor: "#D4AF37" }} />
                  <div className="flex justify-between text-[10px] text-[#444] mt-1"><span>5 yr</span><span>30 yr</span></div>
                </div>
              </div>

              {/* EMI Results */}
              <div className="border border-[#D4AF37]/10 bg-[#0a0900]/50 p-6 mb-8">
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#555] mb-5">Loan Breakdown</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <p className="text-[10px] tracking-[0.2em] text-[#666] uppercase mb-1">Monthly EMI</p>
                    <p className="font-display text-2xl text-[#D4AF37]">{formatINR(emi)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.2em] text-[#666] uppercase mb-1">Loan Amount</p>
                    <p className="font-display text-2xl text-[#FAF8F3]">{formatINR(loanAmount)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.2em] text-[#666] uppercase mb-1">Total Interest</p>
                    <p className="font-display text-2xl text-[#FAF8F3]">{formatINR(totalInterest)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.2em] text-[#666] uppercase mb-1">Total Payable</p>
                    <p className="font-display text-2xl text-[#FAF8F3]">{formatINR(totalPayable)}</p>
                  </div>
                </div>

                {/* Visual: Principal vs Interest ratio bar */}
                <div className="mt-6">
                  <div className="flex gap-1 h-3 rounded-full overflow-hidden">
                    <div className="bg-[#D4AF37] rounded-l-full transition-all duration-500"
                      style={{ width: `${(loanAmount / totalPayable) * 100}%` }} />
                    <div className="bg-[#D4AF37]/20 rounded-r-full flex-1" />
                  </div>
                  <div className="flex justify-between mt-2 text-[9px] text-[#555]">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#D4AF37] rounded-sm inline-block" /> Principal {Math.round((loanAmount / totalPayable) * 100)}%</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#D4AF37]/20 border border-[#D4AF37]/30 rounded-sm inline-block" /> Interest {Math.round((totalInterest / totalPayable) * 100)}%</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-t border-white/5 pt-6">
                <p className="text-xs text-[#555] max-w-md">
                  EMI calculations are indicative. Actual rates may vary based on lender, credit score, and property type. Consult our advisors for bank introductions.
                </p>
                <Link to="/consultation" className="btn-gold text-xs py-3 px-6 shrink-0">
                  Get Loan Advisory <ArrowRight size={12} className="inline ml-2" />
                </Link>
              </div>
            </>
          )}
        </motion.div>

        {/* Lead capture modal (ROI tab only) */}
        <AnimatePresence>
          {leadCapture && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-6"
              onClick={(e) => e.target === e.currentTarget && setLeadCapture(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="glass-gold p-8 max-w-md w-full relative"
              >
                <button onClick={() => setLeadCapture(false)} className="absolute top-4 right-4 text-[#666] hover:text-[#FAF8F3]">
                  <X size={18} />
                </button>
                <p className="section-label mb-2">Almost There</p>
                <h3 className="font-display text-2xl text-[#FAF8F3] mb-1">Unlock Your Full Report</h3>
                <p className="text-[#555] text-xs mb-6">
                  {typeRates[propType].label} · {formatINR(budget)} · {years} years · Est. ROI: <span className="text-[#D4AF37]">{totalROI}%</span>
                </p>
                <form onSubmit={handleLeadSubmit} className="space-y-5">
                  <div>
                    <label className="text-xs text-[#A0A0A0] tracking-wider uppercase mb-2 block">Your Name</label>
                    <input value={leadName} onChange={(e) => setLeadName(e.target.value)}
                      className="input-luxury" placeholder="Full Name" required />
                  </div>
                  <div>
                    <label className="text-xs text-[#A0A0A0] tracking-wider uppercase mb-2 block">Phone Number</label>
                    <input value={leadPhone} onChange={(e) => setLeadPhone(e.target.value)}
                      className="input-luxury" placeholder="+91 XXXXX XXXXX" required />
                  </div>
                  <button type="submit" className="btn-gold-solid btn-gold w-full justify-center mt-4">
                    View My Investment Report
                  </button>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

// ======================== SECTION 6: INFRA STORYTELLING ========================
const InfraStorytelling = () => {
  const scenes = [
    {
      icon: Plane,
      title: "Airport Transforms Everything",
      subtitle: "Navi Mumbai International Airport",
      desc: "India's largest greenfield airport under construction. 25km influence zone creating India's most attractive real estate appreciation corridor. First mover advantage still available.",
      stat: "+200% appreciation potential in airport zone",
      bg: "from-[#080808] via-[#0a0700] to-[#080808]",
      image: "https://images.pexels.com/photos/6604557/pexels-photo-6604557.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      tag: "01 / Airport",
      data1: "2025", label1: "Target Launch",
      data2: "25km", label2: "Influence Zone",
    },
    {
      icon: Landmark,
      title: "Connected Like Never Before",
      subtitle: "Atal Setu Sea Bridge",
      desc: "India's longest 21.8km sea bridge now operational. Mumbai to Navi Mumbai in 20 minutes. The connectivity revolution has already begun – and property prices are responding.",
      stat: "2 hours to 20 minutes. 300% connectivity boost.",
      bg: "from-[#080808] via-[#070a08] to-[#080808]",
      image: "https://images.unsplash.com/photo-1708357086795-7f36fb996c35?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxBdGFsJTIwU2V0dSUyMHNlYSUyMGJyaWRnZSUyME11bWJhaSUyMGFlcmlhbCUyMGRyb25lfGVufDB8fHx8MTc4MjI4MjYyNnww&ixlib=rb-4.1.0&q=85",
      tag: "02 / Bridge",
      data1: "2024", label1: "Operational",
      data2: "21.8km", label2: "Sea Bridge Length",
    },
    {
      icon: Train,
      title: "Metro-Led Rental Growth",
      subtitle: "Navi Mumbai Metro Network",
      desc: "6 metro corridors creating 36+ stations across Navi Mumbai. Properties within 500m of metro stations command 40% rental premium. Smart investors are buying the adjacency.",
      stat: "+40% rental yield near metro stations",
      bg: "from-[#080808] via-[#08070a] to-[#080808]",
      image: "https://images.pexels.com/photos/37790959/pexels-photo-37790959.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      tag: "03 / Metro",
      data1: "36+", label1: "Stations",
      data2: "6", label2: "Metro Corridors",
    },
    {
      icon: Building2,
      title: "A Smart City Is Born",
      subtitle: "NAINA Development Zone",
      desc: "30,000 hectares of planned development. ₹3 lakh crore infrastructure investment. A brand new smart city serving 30 lakh residents by 2035. The ground floor moment is now.",
      stat: "₹3 Lakh Cr investment. 30,000 hectares.",
      bg: "from-[#080808] via-[#0a0a07] to-[#080808]",
      image: "https://images.pexels.com/photos/34842268/pexels-photo-34842268.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      tag: "04 / NAINA",
      data1: "2035", label1: "Completion",
      data2: "30k Ha", label2: "Development Area",
    },
  ];

  return (
    <section className="bg-[#060606]">
      {scenes.map((s, i) => (
        <motion.div key={i}
          className={`py-24 md:py-32 bg-gradient-to-b ${s.bg} border-b border-white/5`}
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${i % 2 === 1 ? "direction-rtl" : ""}`}>
              <motion.div {...fadeUp} className={i % 2 === 1 ? "lg:order-2" : ""}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 border border-[#D4AF37]/30 flex items-center justify-center">
                    <s.icon size={20} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.3em] text-[#D4AF37] uppercase">{`0${i + 1} / 04`}</p>
                    <p className="text-xs text-[#666]">{s.subtitle}</p>
                  </div>
                </div>
                <h2 className="font-display text-3xl md:text-4xl font-light text-[#FAF8F3] mb-4">{s.title}</h2>
                <p className="text-[#A0A0A0] text-sm leading-relaxed mb-6">{s.desc}</p>
                <div className="border-l-2 border-[#D4AF37] pl-4">
                  <p className="text-[#D4AF37] text-sm font-light italic">{s.stat}</p>
                </div>
              </motion.div>

              <motion.div {...fadeUp} transition={{ delay: 0.15 }}
                className={`relative overflow-hidden rounded-sm ${i % 2 === 1 ? "lg:order-1" : ""}`}
              >
                {/* Real infrastructure image */}
                <div className="aspect-video relative overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.subtitle}
                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    style={{ filter: "brightness(0.75) saturate(0.9)" }}
                  />
                  {/* Luxury overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#080808]/80 via-transparent to-transparent" />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#080808]/30 via-transparent to-transparent" />
                  {/* Tag label */}
                  <div className="absolute top-4 left-4 text-[10px] tracking-[0.3em] uppercase px-3 py-1 border border-[#D4AF37]/40 text-[#D4AF37] bg-black/40 backdrop-blur-sm">
                    {s.tag}
                  </div>
                  {/* Gold border accent */}
                  <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#D4AF37]/60 to-transparent" />
                </div>
                {/* Stats bar below image */}
                <div className="grid grid-cols-2 gap-0 border border-[#D4AF37]/10 border-t-0">
                  <div className="border-r border-[#D4AF37]/10 p-4 bg-[#0a0900]">
                    <p className="text-[#D4AF37] text-xl font-display font-light">{s.data1}</p>
                    <p className="text-[#555] text-[10px] tracking-wider mt-1 uppercase">{s.label1}</p>
                  </div>
                  <div className="p-4 bg-[#0a0900]">
                    <p className="text-[#D4AF37] text-xl font-display font-light">{s.data2}</p>
                    <p className="text-[#555] text-[10px] tracking-wider mt-1 uppercase">{s.label2}</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      ))}
    </section>
  );
};

// ======================== SECTION 7: FEATURED PROPERTIES ========================
const FeaturedProperties = () => {
  const [properties, setProperties] = useState([]);
  const [activeTag, setActiveTag] = useState("All");
  const tags = ["All", "Luxury", "Investor Picks", "Pre-launch", "Villas", "Commercial"];

  useEffect(() => {
    axios.get(`${API}/properties`).then((r) => setProperties(r.data)).catch(() => {});
  }, []);

  const filtered = activeTag === "All" ? properties : properties.filter((p) => p.tags?.includes(activeTag));

  return (
    <section className="py-24 md:py-32 bg-[#080808]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div>
            <motion.p {...fadeUp} className="section-label">Curated Opportunities</motion.p>
            <motion.h2 {...fadeUp} transition={{ delay: 0.1 }}
              className="font-display text-4xl md:text-5xl font-light text-[#FAF8F3]">
              Hand-Picked for<br />
              <span className="text-gold-gradient italic">Discerning Investors</span>
            </motion.h2>
          </div>
          <Link to="/properties" className="btn-gold text-xs py-3 px-6 self-start md:self-auto shrink-0">
            All Properties <ArrowRight size={12} className="inline ml-2" />
          </Link>
        </div>

        {/* Filter tags */}
        <motion.div {...fadeUp} className="flex flex-wrap gap-3 mb-10">
          {tags.map((t) => (
            <button key={t} onClick={() => setActiveTag(t)}
              className={`text-xs px-5 py-2 border tracking-[0.15em] uppercase transition-all duration-300 ${
                activeTag === t ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5" : "border-white/10 text-[#666] hover:border-white/30"
              }`}
            >
              {t}
            </button>
          ))}
        </motion.div>

        {/* Property cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.slice(0, 6).map((p, i) => (
            <motion.div key={p.id}
              {...fadeUp} transition={{ delay: i * 0.08, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <Link to={`/properties/${p.id}`} className="block gold-card bg-[#0a0a0a] group">
                {/* Image */}
                <div className="relative h-52 overflow-hidden">
                  <img src={p.image} alt={p.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
                  {/* Tags */}
                  <div className="absolute top-3 left-3 flex gap-2">
                    {(p.tags || []).slice(0, 2).map((tag) => (
                      <span key={tag} className="text-[9px] tracking-wider uppercase px-2 py-1 bg-black/60 text-[#D4AF37] border border-[#D4AF37]/20">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="absolute bottom-3 right-3 text-right">
                    <p className="font-display text-lg text-[#D4AF37]">{p.price_display}</p>
                  </div>
                </div>

                <div className="p-5">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-heading text-[#FAF8F3] font-light text-base mb-1 group-hover:text-[#D4AF37] transition-colors">{p.title}</h3>
                      <div className="flex items-center gap-1 text-xs text-[#666]">
                        <MapPin size={10} className="text-[#D4AF37]/60 shrink-0" />
                        <span>{p.location}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5">
                    <div className="flex gap-4">
                      <div className="text-center">
                        <p className="text-[#D4AF37] text-sm font-display">{p.roi_score}</p>
                        <p className="text-[#444] text-[9px] tracking-wider">ROI Score</p>
                      </div>
                      <div className="text-center">
                        <p className="text-[#D4AF37] text-sm font-display">{p.growth_score}</p>
                        <p className="text-[#444] text-[9px] tracking-wider">Growth</p>
                      </div>
                      <div className="text-center">
                        <p className="text-[#D4AF37] text-sm font-display">{p.annual_rental_yield}</p>
                        <p className="text-[#444] text-[9px] tracking-wider">Yield</p>
                      </div>
                    </div>
                    <span className="text-[10px] text-[#666] border border-white/10 px-2 py-1">{p.status}</span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ======================== SECTION 8: ELITE DEVELOPERS ========================
const EliteDevelopers = () => {
  const [developers, setDevelopers] = useState([]);
  const [hoveredDev, setHoveredDev] = useState(null);

  useEffect(() => {
    axios.get(`${API}/developers`).then((r) => setDevelopers(r.data)).catch(() => {});
  }, []);

  return (
    <section className="py-24 bg-[#060606]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <motion.p {...fadeUp} className="section-label">Our Developer Partners</motion.p>
          <motion.h2 {...fadeUp} transition={{ delay: 0.1 }}
            className="font-display text-4xl font-light text-[#FAF8F3]">
            India's Most Trusted<br />
            <span className="text-gold-gradient italic">Real Estate Groups</span>
          </motion.h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {developers.map((dev, i) => (
            <motion.div key={dev.id}
              {...fadeUp} transition={{ delay: i * 0.08 }}
              onMouseEnter={() => setHoveredDev(dev.id)}
              onMouseLeave={() => setHoveredDev(null)}
              className="gold-card glass-gold p-6 cursor-pointer relative overflow-hidden group"
            >
              <div className="flex items-center gap-4 mb-3">
                <div className="w-10 h-10 flex items-center justify-center text-[#D4AF37] text-xs font-heading font-semibold border border-[#D4AF37]/20"
                  style={{ background: `${dev.logo_color}15` }}>
                  {dev.name.charAt(0)}
                </div>
                <div>
                  <h3 className="text-sm font-heading text-[#FAF8F3] font-light">{dev.name}</h3>
                  <p className="text-[10px] text-[#666]">{dev.tagline}</p>
                </div>
              </div>

              <AnimatePresence>
                {hoveredDev === dev.id && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
                    className="mt-3 pt-3 border-t border-white/5"
                  >
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-[#D4AF37] text-sm font-display">{dev.avg_appreciation}</p>
                        <p className="text-[#444] text-[9px] tracking-wider">Avg Appreciation</p>
                      </div>
                      <div>
                        <p className="text-[#D4AF37] text-sm font-display">{dev.delivery_record}</p>
                        <p className="text-[#444] text-[9px] tracking-wider">Delivery Record</p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-8">
          <Link to="/developers" className="btn-gold text-xs py-3 px-8">
            View All Developers <ArrowRight size={12} className="inline ml-2" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// ======================== SECTION 9: DASHBOARD PREVIEW ========================
const DashboardPreview = () => (
  <section className="py-24 md:py-32 bg-[#080808] overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <motion.p {...fadeUp} className="section-label">Investor Platform</motion.p>
          <motion.h2 {...fadeUp} transition={{ delay: 0.1 }}
            className="font-display text-4xl md:text-5xl font-light text-[#FAF8F3] mb-6">
            Your Wealth<br />
            <span className="text-gold-gradient italic">Command Center</span>
          </motion.h2>
          <motion.p {...fadeUp} transition={{ delay: 0.2 }}
            className="text-[#A0A0A0] text-sm leading-relaxed mb-8">
            Track your investment portfolio, monitor market intelligence, and receive curated alerts — all from your personalized investor dashboard.
          </motion.p>
          {["Saved Properties & Comparisons", "Live ROI & Appreciation Tracker", "Infrastructure Progress Updates", "Site Visit Scheduling", "Market Intelligence Reports"].map((f, i) => (
            <motion.div key={f} {...fadeUp} transition={{ delay: 0.2 + i * 0.08 }}
              className="flex items-center gap-3 mb-3">
              <CheckCircle size={14} className="text-[#D4AF37] shrink-0" />
              <span className="text-sm text-[#A0A0A0] font-light">{f}</span>
            </motion.div>
          ))}
          <motion.div {...fadeUp} transition={{ delay: 0.6 }} className="mt-8">
            <Link to="/consultation" className="btn-gold text-xs py-3 px-8">
              Get Early Access <ArrowRight size={12} className="inline ml-2" />
            </Link>
          </motion.div>
        </div>

        {/* Mock dashboard UI */}
        <motion.div {...fadeUp} transition={{ delay: 0.2 }} className="glass-gold p-1">
          <div className="bg-[#0a0a0a] p-4">
            <div className="flex items-center gap-3 border-b border-white/5 pb-3 mb-4">
              {["Portfolio", "ROI Tracker", "Market Intel", "Site Visits"].map((tab, i) => (
                <button key={tab} className={`text-[10px] tracking-wider uppercase pb-2 border-b-2 transition-all ${
                  i === 0 ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-[#444]"
                }`}>{tab}</button>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[
                { label: "Portfolio Value", value: "₹2.4 Cr" },
                { label: "Total ROI", value: "+68%" },
                { label: "Properties", value: "3" },
              ].map((m) => (
                <div key={m.label} className="border border-white/5 p-3">
                  <p className="font-display text-lg text-[#D4AF37]">{m.value}</p>
                  <p className="text-[9px] text-[#444] tracking-wider mt-1">{m.label}</p>
                </div>
              ))}
            </div>
            <div className="space-y-2">
              {["Prestige Smart City – Ulwe", "Godrej Prime – Kharghar"].map((p) => (
                <div key={p} className="flex items-center justify-between border border-white/5 p-3">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-[#D4AF37]/10 flex items-center justify-center">
                      <Building2 size={10} className="text-[#D4AF37]" />
                    </div>
                    <span className="text-[11px] text-[#FAF8F3]/70">{p}</span>
                  </div>
                  <span className="text-[10px] text-[#D4AF37]">+34%</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

// ======================== SECTION 10: SUCCESS STORIES ========================
const SuccessStories = () => {
  const stories = [
    {
      name: "Rajiv Mehta", role: "IT Professional, Pune",
      story: "Invested ₹75L in Kharghar in 2021. Current value ₹1.4 Cr. ROI of 86% in 3 years. Sneha Reality's infrastructure analysis was spot on.",
      investment: "₹75 Lakh", returns: "₹1.4 Cr", timeframe: "3 Years", rating: 5,
    },
    {
      name: "Priya Kapoor", role: "NRI Investor, Dubai",
      story: "Bought 2 units in Ulwe pre-launch based on Sneha Reality's airport proximity report. Already 45% appreciation before possession.",
      investment: "₹1.2 Cr", returns: "₹1.74 Cr", timeframe: "2 Years", rating: 5,
    },
    {
      name: "Anil Sharma", role: "Business Owner, Mumbai",
      story: "Diversified into commercial in Panvel corridor. Rental yield of 7.2% and property appreciation of 60% over 4 years. Best investment decision.",
      investment: "₹2 Cr", returns: "₹3.2 Cr", timeframe: "4 Years", rating: 5,
    },
  ];

  return (
    <section className="py-24 bg-[#060606]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <motion.p {...fadeUp} className="section-label">Investor Success</motion.p>
          <motion.h2 {...fadeUp} transition={{ delay: 0.1 }}
            className="font-display text-4xl font-light text-[#FAF8F3]">
            Wealth Created.<br />
            <span className="text-gold-gradient italic">Lives Transformed.</span>
          </motion.h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {stories.map((s, i) => (
            <motion.div key={s.name}
              {...fadeUp} transition={{ delay: i * 0.12 }}
              className="gold-card glass-gold p-8"
            >
              <div className="flex mb-4">
                {[...Array(s.rating)].map((_, j) => (
                  <Star key={j} size={12} className="text-[#D4AF37] fill-[#D4AF37]" />
                ))}
              </div>
              <p className="text-[#A0A0A0] text-sm leading-relaxed mb-6 italic">"{s.story}"</p>
              <div className="grid grid-cols-3 gap-2 mb-6 pt-4 border-t border-white/5">
                <div>
                  <p className="text-[#D4AF37] text-sm font-display">{s.investment}</p>
                  <p className="text-[#444] text-[9px] tracking-wider">Invested</p>
                </div>
                <div>
                  <p className="text-[#D4AF37] text-sm font-display">{s.returns}</p>
                  <p className="text-[#444] text-[9px] tracking-wider">Current Value</p>
                </div>
                <div>
                  <p className="text-[#D4AF37] text-sm font-display">{s.timeframe}</p>
                  <p className="text-[#444] text-[9px] tracking-wider">Timeframe</p>
                </div>
              </div>
              <div>
                <p className="text-[#FAF8F3] text-sm font-light">{s.name}</p>
                <p className="text-[#666] text-xs">{s.role}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ======================== SECTION 11: CONSULTATION FORM ========================
const ConsultationFormSection = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", budget: "", timeline: "" });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, lead_type: "consultation" });
      setStatus("success");
      setForm({ name: "", phone: "", email: "", budget: "", timeline: "" });
    } catch {
      setStatus("error");
    }
    setLoading(false);
  };

  return (
    <section id="consultation" data-testid={HOME.consultationForm}
      className="py-24 md:py-32 relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #080808 0%, #0d0b00 50%, #080808 100%)" }}
    >
      <div className="absolute inset-0 opacity-30"
        style={{ background: "radial-gradient(ellipse 60% 60% at 50% 50%, rgba(212,175,55,0.08), transparent)" }} />

      <div className="max-w-3xl mx-auto px-6 md:px-12 relative z-10 text-center">
        <motion.p {...fadeUp} className="section-label">Private Advisory</motion.p>
        <motion.h2 {...fadeUp} transition={{ delay: 0.1 }}
          className="font-display text-4xl md:text-5xl font-light text-[#FAF8F3] mb-4">
          Your Wealth Journey<br />
          <span className="text-gold-gradient italic">Starts Here</span>
        </motion.h2>
        <motion.p {...fadeUp} transition={{ delay: 0.2 }}
          className="text-[#666] text-sm mb-10">
          Connect with our expert advisory team for a personalized investment consultation.
        </motion.p>

        {status === "success" ? (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            data-testid={FORMS.successMsg}
            className="glass-gold p-10 text-center"
          >
            <CheckCircle size={40} className="text-[#D4AF37] mx-auto mb-4" />
            <h3 className="font-display text-2xl text-[#FAF8F3] mb-2">Request Received</h3>
            <p className="text-[#666] text-sm">Our wealth advisory team will contact you within 24 hours.</p>
          </motion.div>
        ) : (
          <motion.form {...fadeUp} transition={{ delay: 0.3 }} onSubmit={handleSubmit} className="glass-gold p-8 md:p-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="text-xs text-[#A0A0A0] tracking-wider uppercase mb-2 block">Full Name *</label>
                <input data-testid={FORMS.nameInput} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="input-luxury" placeholder="Your full name" required />
              </div>
              <div>
                <label className="text-xs text-[#A0A0A0] tracking-wider uppercase mb-2 block">Phone *</label>
                <input data-testid={FORMS.phoneInput} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="input-luxury" placeholder="+91 XXXXX XXXXX" required />
              </div>
              <div>
                <label className="text-xs text-[#A0A0A0] tracking-wider uppercase mb-2 block">Email</label>
                <input data-testid={FORMS.emailInput} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="input-luxury" type="email" placeholder="your@email.com" />
              </div>
              <div>
                <label className="text-xs text-[#A0A0A0] tracking-wider uppercase mb-2 block">Budget</label>
                <select data-testid={FORMS.budgetSelect} value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })}
                  className="input-luxury bg-transparent"
                >
                  <option value="" className="bg-[#111]">Select Budget</option>
                  <option value="Below 50L" className="bg-[#111]">Below ₹50 Lakh</option>
                  <option value="50L-1Cr" className="bg-[#111]">₹50L – ₹1 Cr</option>
                  <option value="1Cr-2Cr" className="bg-[#111]">₹1 Cr – ₹2 Cr</option>
                  <option value="2Cr-5Cr" className="bg-[#111]">₹2 Cr – ₹5 Cr</option>
                  <option value="5Cr+" className="bg-[#111]">₹5 Cr+</option>
                </select>
              </div>
            </div>
            <button data-testid={FORMS.submitBtn} type="submit" disabled={loading}
              className="btn-gold-solid btn-gold w-full justify-center py-4 text-sm">
              {loading ? "Sending..." : "Request Private Advisory Session"}
              {!loading && <ArrowRight size={14} className="inline ml-2" />}
            </button>
            {status === "error" && (
              <p className="text-red-400 text-xs text-center mt-4">Something went wrong. Please call us directly.</p>
            )}
          </motion.form>
        )}
      </div>
    </section>
  );
};

// ======================== SECTION 12: AI PROPERTY MATCH ========================
const AIPropertyMatch = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ budget: "", purpose: "", family_size: "", timeline: "", priority: "" });
  const [aiResponse, setAiResponse] = useState("");
  const [loading, setLoading] = useState(false);

  const questions = [
    { key: "budget", label: "What's your investment budget?", options: ["₹25L – ₹75L", "₹75L – ₹1.5 Cr", "₹1.5 Cr – ₹3 Cr", "₹3 Cr – ₹5 Cr", "₹5 Cr+"] },
    { key: "purpose", label: "Investment purpose?", options: ["Capital Appreciation", "Rental Income", "Self Use + Investment", "Business Expansion", "NRI Investment"] },
    { key: "family_size", label: "Your profile?", options: ["Single Investor", "Young Couple", "Family (3-4 members)", "Large Family (5+)", "Corporate/HNI"] },
    { key: "timeline", label: "Investment timeline?", options: ["1-2 Years (Short)", "3-5 Years (Medium)", "5-8 Years (Long)", "8+ Years (Wealth Creation)"] },
    { key: "priority", label: "Your top priority?", options: ["Maximum ROI", "Low Risk / Steady Growth", "Rental Yield", "Infrastructure Proximity", "Premium Lifestyle"] },
  ];

  const currentQ = questions[step];

  const handleAnswer = (val) => {
    const newAnswers = { ...answers, [currentQ.key]: val };
    setAnswers(newAnswers);
    if (step < questions.length - 1) {
      setStep(step + 1);
    } else {
      getAIMatch(newAnswers);
    }
  };

  const getAIMatch = async (finalAnswers) => {
    setLoading(true);
    setAiResponse("");
    try {
      const res = await fetch(`${API}/ai-match`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(finalAnswers),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const text = decoder.decode(value);
        text.split("\n").forEach((line) => {
          if (line.startsWith("data: ")) {
            const content = line.slice(6);
            if (content !== "[DONE]") setAiResponse((prev) => prev + content);
          }
        });
      }
    } catch {
      setAiResponse("Based on your profile, our team recommends exploring properties in Ulwe and Kharghar. Contact us at +91 7977009190 for a personalized consultation.");
    }
    setLoading(false);
  };

  const reset = () => { setStep(0); setAnswers({ budget: "", purpose: "", family_size: "", timeline: "", priority: "" }); setAiResponse(""); setLoading(false); };

  return (
    <>
      {/* Floating AI Match button — quick access */}
      <div className="fixed bottom-24 right-6 z-50">
        <button data-testid={HOME.aiMatchBtn} onClick={() => setOpen(true)}
          className="flex items-center gap-2 bg-[#0a0900] border border-[#D4AF37]/40 text-[#D4AF37] px-4 py-3 text-xs tracking-wider uppercase hover:bg-[#D4AF37]/10 transition-all duration-300 font-heading"
          style={{ boxShadow: "0 0 20px rgba(212,175,55,0.1)" }}
        >
          <Sparkles size={12} className="animate-glow" />
          <span className="hidden md:inline">AI Property Match</span>
        </button>
      </div>

      {/* Full section — AI Property Match */}
      <section className="py-24 md:py-32 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #060606 0%, #080808 50%, #060606 100%)" }}>
        {/* Background texture */}
        <div className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: "radial-gradient(circle, rgba(212,175,55,1) 1px, transparent 1px)",
            backgroundSize: "40px 40px"
          }} />
        <div className="absolute inset-0"
          style={{ background: "radial-gradient(ellipse 70% 50% at 50% 50%, rgba(212,175,55,0.04), transparent)" }} />

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left: Content */}
            <div>
              <motion.div {...fadeUp} className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 border border-[#D4AF37]/30 flex items-center justify-center">
                  <Sparkles size={14} className="text-[#D4AF37]" />
                </div>
                <span className="section-label">AI-Powered Advisory</span>
              </motion.div>
              <motion.h2 {...fadeUp} transition={{ delay: 0.1 }}
                className="font-display text-4xl md:text-5xl font-light text-[#FAF8F3] mb-6">
                Meet Aiden —<br />
                <span className="text-gold-gradient italic">Your AI Investment Advisor</span>
              </motion.h2>
              <motion.p {...fadeUp} transition={{ delay: 0.2 }}
                className="text-[#A0A0A0] text-sm leading-relaxed mb-8">
                Answer 5 quick questions and Aiden — powered by Claude AI — will analyze your financial profile, risk appetite, and goals to recommend the perfect Navi Mumbai properties for your portfolio.
              </motion.p>
              <motion.ul {...fadeUp} transition={{ delay: 0.3 }} className="space-y-3 mb-10">
                {[
                  "Personalized analysis based on your unique investor profile",
                  "ROI projections aligned to infrastructure timelines",
                  "Risk-adjusted recommendations across property types",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[#A0A0A0]">
                    <CheckCircle size={14} className="text-[#D4AF37] mt-0.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </motion.ul>
              <motion.button
                {...fadeUp} transition={{ delay: 0.4 }}
                onClick={() => setOpen(true)}
                className="btn-gold-solid btn-gold group"
              >
                <Sparkles size={14} className="mr-2" />
                Start AI Property Match
                <ArrowRight size={14} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </motion.button>
            </div>

            {/* Right: Visual card */}
            <motion.div {...fadeUp} transition={{ delay: 0.2 }} className="glass-gold p-8 relative">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center rounded-full">
                  <Sparkles size={16} className="text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-[#FAF8F3] text-sm font-light">Aiden AI</p>
                  <p className="text-[10px] text-[#D4AF37] tracking-wider uppercase">Investment Advisor</p>
                </div>
              </div>

              {/* Mock chat bubbles */}
              <div className="space-y-4 mb-6">
                <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/10 p-4">
                  <p className="text-xs text-[#A0A0A0] italic leading-relaxed">
                    "Based on your ₹1.5 Cr budget, 5-year horizon, and preference for capital appreciation — I recommend <span className="text-[#D4AF37]">pre-launch flats near NMIA Airport zone</span> for maximum upside..."
                  </p>
                </div>
                <div className="bg-white/[0.02] border border-white/5 p-4">
                  <p className="text-xs text-[#666] italic leading-relaxed">
                    "The Ulwe micromarket is currently 4X undervalued vs South Mumbai with direct airport adjacency. Expected 18% CAGR over 5 years..."
                  </p>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3 border-t border-white/5 pt-5">
                {[
                  { val: "5", label: "Questions" },
                  { val: "30s", label: "Analysis" },
                  { val: "AI", label: "Powered" },
                ].map((s) => (
                  <div key={s.label} className="text-center">
                    <p className="font-display text-2xl text-[#D4AF37] font-light">{s.val}</p>
                    <p className="text-[9px] text-[#555] tracking-[0.2em] uppercase mt-1">{s.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 backdrop-blur-sm px-6"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="glass-gold p-8 max-w-xl w-full relative max-h-[90vh] overflow-y-auto"
            >
              <button onClick={() => { setOpen(false); reset(); }}
                className="absolute top-4 right-4 text-[#666] hover:text-[#FAF8F3] transition-colors">
                <X size={18} />
              </button>

              {!aiResponse && !loading ? (
                <>
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Sparkles size={16} className="text-[#D4AF37]" />
                      <span className="section-label">AI Property Match — Aiden</span>
                    </div>
                    <h3 className="font-display text-2xl text-[#FAF8F3] mb-1">
                      {currentQ.label}
                    </h3>
                    <p className="text-[10px] text-[#555] tracking-wider mb-4">Question {step + 1} of {questions.length}</p>
                    <div className="flex gap-1">
                      {questions.map((_, i) => (
                        <div key={i} className={`h-[2px] flex-1 transition-all duration-300 ${i <= step ? "bg-[#D4AF37]" : "bg-white/10"}`} />
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    {currentQ.options.map((opt) => (
                      <button key={opt} onClick={() => handleAnswer(opt)}
                        className="text-left p-4 border border-white/10 hover:border-[#D4AF37]/50 hover:bg-[#D4AF37]/5 transition-all duration-200 text-sm text-[#FAF8F3] font-light">
                        {opt}
                      </button>
                    ))}
                  </div>
                </>
              ) : loading ? (
                <div className="text-center py-12">
                  <div className="w-12 h-12 border border-[#D4AF37]/20 border-t-[#D4AF37] rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-[#D4AF37] text-sm tracking-wider">Aiden is analyzing your profile...</p>
                  <p className="text-[#555] text-xs mt-2">Powered by Claude AI</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles size={16} className="text-[#D4AF37]" />
                    <span className="section-label">Aiden's Recommendation</span>
                  </div>
                  <div className="prose-custom text-[#A0A0A0] text-sm leading-relaxed whitespace-pre-wrap mb-6 max-h-64 overflow-y-auto">
                    {aiResponse}
                  </div>
                  <div className="flex gap-3 flex-wrap">
                    <Link to="/properties" onClick={() => setOpen(false)} className="btn-gold-solid btn-gold text-xs py-3 px-6">
                      Explore Properties <ArrowRight size={12} className="inline ml-1" />
                    </Link>
                    <button onClick={reset} className="btn-gold text-xs py-3 px-4">
                      Try Again
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ======================== MAIN HOME COMPONENT ========================
const Home = () => (
  <div className="bg-[#080808] text-[#FAF8F3]">
    <HeroSection />
    <WealthCounter />
    <InfrastructureMap />
    <WhyNaviMumbaiSection />
    <ROICalculator />
    <InfraStorytelling />
    <FeaturedProperties />
    <EliteDevelopers />
    <DashboardPreview />
    <SuccessStories />
    <ConsultationFormSection />
    <AIPropertyMatch />
  </div>
);

export default Home;
